import React, { useEffect, useState } from 'react'
import Layout from '../components/Layout/Layout'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Line, Pie } from 'react-chartjs-2'
import LowQuantityProduct from '../components/common/LowQuantityProduct'
import RecentOrders from '../components/common/RecentOrders'
import TopProducts from '../components/common/TopProducts'
import TopCustomers from '../components/common/TopCustomers'
import { callApi } from '../api/callApi'
import { API_ENDPOINT, API_URL_2 } from '../api/constant'
import { generateColors, toFixedThree } from '../utility/helper'

ChartJS.register(...registerables)

const Dashboard: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<{ order: any, products: any }>({
        order: null,
        products: null
    });


    useEffect(() => {
        fetchDashboardData()
    }, [])

    const fetchDashboardData = async () => {
        const [order, products] = await Promise.all([
            callApi(API_URL_2 + API_ENDPOINT.order + 'dashboard', { method: 'GET' }),
            callApi(API_URL_2 + API_ENDPOINT.page + 'dashboard', { method: 'GET' })
        ])

        setDashboardData({
            order: order,
            products: products
        })
    }

    // Earning Chart Data
    const processEarningData = (dateWiseCount: any) => {
        const sortedDates = Object.keys(dateWiseCount).sort();
        return {
            labels: sortedDates,
            data: sortedDates.map(date => dateWiseCount[date].sales)
        };
    };

    const processCategoryData = (categoryWiseCount: any) => {
        const labels = Object.keys(categoryWiseCount);
        const data = labels.map(category => categoryWiseCount[category].sales);
        return { labels, data };
    };

    const { labels: earningLabels, data: earningValues } = processEarningData(dashboardData.order?.dateWiseCount ? dashboardData.order?.dateWiseCount : {});
    const { labels: categoryLabels, data: categoryValues } = processCategoryData(dashboardData.order?.categoryWiseCount ? dashboardData.order?.categoryWiseCount : {});

    return (
        <Layout>
            <h2 className="app-page-title">Dashboard Overview</h2>

            {/* Stats Cards */}
            <div className="row g-4 mb-2 dashboardcards">
                {[
                    {
                        title: "Total Sales",
                        value: "BHD " + toFixedThree(dashboardData.order?.totalSales || 0),
                        color: "text-success",
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-bar-chart" viewBox="0 0 16 16"><path d="M4 11H2v3h2zm5-4H7v7h2zm5-5v12h-2V2zm-2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM6 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm-5 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1z"/></svg>`
                    },
                    {
                        title: "Pending Orders",
                        value: dashboardData.order?.pendingOrdersCount || 0,
                        color: "text-danger",
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-box" viewBox="0 0 16 16"><path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464z"/></svg>`
                    },
                    {
                        title: "Products",
                        value: dashboardData.products?.products || 0,
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-cart3" viewBox="0 0 16 16"><path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l.84 4.479 9.144-.459L13.89 4zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/></svg>`
                    },
                    {
                        title: "Total Orders",
                        value: dashboardData.order?.totalOrdersCount || 0,
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-card-list" viewBox="0 0 16 16"><path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2z"></path><path d="M5 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 5 8m0-2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5m0 5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5m-1-5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0M4 8a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0m0 2.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0"></path></svg>`
                    },
                    {
                        title: 'Active/Verified Users',
                        value: dashboardData.order?.userCount?.verified || 0,
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-people" viewBox="0 0 16 16"><path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72a.261.261 0 0 1-.022.004H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5.341 9c-4.673 0-5.468 3.247-5.468 4.166V14h4.75c.312-.374.588-.83.793-1.346C5.973 11.6 6.203 10.668 6.936 9.28zM4.5 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-2-3a2 2 0 1 1 4 0 2 2 0 0 1-4 0z"/></svg>`
                    },
                    {
                        title: "Inactive/Unverified Users",
                        value: dashboardData.order?.userCount?.unverified || 0,
                        icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-person-x" viewBox="0 0 16 16"><path d="M6 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm4.146-2.354a.5.5 0 0 1 .708 0L13 8.793l2.146-2.147a.5.5 0 0 1 .708.708L13.707 9.5l2.147 2.146a.5.5 0 0 1-.708.708L13 10.207l-2.146 2.147a.5.5 0 0 1-.708-.708L12.293 9.5l-2.147-2.146a.5.5 0 0 1 0-.708z"/><path d="M4.216 14A2.238 2.238 0 0 1 2 11.784c0-1.355 2.68-2.464 6-2.464s6 1.109 6 2.464A2.238 2.238 0 0 1 11.784 14H4.216z"/></svg>`
                    }
                ].map((card, index) => (
                    <div className="col-md-3 cardsnew" key={index}>
                        <div className="app-card innrecard app-card-orders-table shadow-sm mb-4 p-4">
                            <h5 className='text-primary d-flex align-items-center justify-content-between gap-3'>
                                {card.title}
                                <span dangerouslySetInnerHTML={{ __html: card.icon }} />
                            </h5>
                            <p>{card.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tables Section */}
            <div className="row g-4 mb-4">
                <div className="col-md-12">
                    <RecentOrders />
                </div>
                <div className="col-md-12">
                    <LowQuantityProduct />
                </div>
                <div className="col-md-8">
                    <TopProducts products={dashboardData.order?.productWiseCount} />
                </div>
                <div className="col-md-4">
                    <div className="app-card app-card-orders-table shadow-sm mb-0 p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="card-title mb-0">Sales by Category</h5>
                        </div>
                        <Pie
                            data={{
                                labels: categoryLabels,
                                datasets: [{
                                    data: categoryValues,
                                    backgroundColor: generateColors(categoryLabels.length),
                                }]
                            }}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <TopCustomers
                        customers={dashboardData.order?.topCustomersByOrders}
                        heading='Top Customers (Spending)'
                        type='sales'
                    />
                </div>
                <div className="col-md-6">
                    <TopCustomers
                        customers={dashboardData.order?.topCustomersBySales}
                        heading='Top Customers (Order Count)'
                        type='orders'
                    />
                </div>
            </div>

            {/* Charts Section */}
            <h2 className="app-page-title mt-2">Sales Analytics</h2>
            <div className="row g-4 mb-4">
                <div className="col-md-12">
                    <div className="app-card app-card-orders-table shadow-sm mb-0 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="card-title mb-0">Earnings Overview</h5>
                        </div>
                        <Line
                            data={{
                                labels: earningLabels,
                                datasets: [{
                                    label: 'Earnings (BHD)',
                                    data: earningValues,
                                    borderColor: 'rgba(75, 192, 192, 1)',
                                    tension: 0.3,
                                }]
                            }}
                            options={{
                                scales: {
                                    x: {
                                        display: true // Hide x-axis labels
                                    }
                                }
                            }}
                        />
                    </div>
                </div>

            </div>
        </Layout>
    )
}

export default Dashboard